import React, { useState, useMemo, useEffect } from 'react';
import { UserProfile, ReportMetadata } from '../../types';
import { Button } from '../Button';
import { 
    FileText, 
    Filter, 
    Download, 
    User, 
    MapPin, 
    Tag, 
    CheckSquare, 
    Square,
    ChevronRight,
    Search,
    BookOpen,
    Camera,
    ImageOff
} from 'lucide-react';
import { resolveProjectCategories } from '../../services/projectCategories';
import { contractedQuantity } from '../../services/nepPosition';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getNormalizedServices } from '../../services/projectLv';
import { isProjectFinished } from '../../services/projectBilling';
import { flatCoverageHints } from '../../services/flatRateLogic';
import { loadReferenceImageForPdf } from '../../services/projectReferenceImage';
import { fitImageIntoBox, describeFileSize, titelbildVon, ProjectMedia } from '../../services/projectReferenceLogic';
import { listAllProjectMedia } from '../../services/projectMediaService';
import type { ListProject } from '../../services/listProject';

interface Props {
  projects: ListProject[];
  users: UserProfile[];
  currentUser: UserProfile | null;
}

export const AdminReferenceSheetView: React.FC<Props> = ({ projects, currentUser }) => {
    // Kategorienkatalog aus dem Profil, sonst Standard (services/projectCategories.ts).
    const projectCategories = useMemo(() => resolveProjectCategories(currentUser?.settings), [currentUser?.settings]);
    // Search and Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [selectedCoordinator, setSelectedCoordinator] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'finished'>('all');
    
    // Financial range options
    const [volumeRange, setVolumeRange] = useState<'all' | 'lt_10k' | '10k_50k' | '50k_150k' | 'gt_150k' | 'custom'>('all');
    const [minVolume, setMinVolume] = useState('');
    const [maxVolume, setMaxVolume] = useState('');

    // Selection for PDF Batch Exports
    const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
    
    // Single Project Preview State
    const [previewProjectId, setPreviewProjectId] = useState<string | null>(null);

    // PDF Print toggles
    const [showFinancialsInPdf, setShowFinancialsInPdf] = useState(true);
    const [showServicesInPdf, setShowServicesInPdf] = useState(true);
    // Referenzbild + Projektbeschreibung (Betreiber 12.09.2026, Social Media)
    // wandern mit aufs Blatt; das Bild wird fuer das PDF verkleinert
    // (loadReferenceImageForPdf), das Original bleibt im Bucket.
    const [showImageInPdf, setShowImageInPdf] = useState(true);
    const [pdfBusy, setPdfBusy] = useState(false);
    // Projektbilder aus `project_media` (Nr. 99) — EINE Abfrage fuer alle
    // Projekte; das Titelbild je Projekt landet auf dem Blatt. Fail-open:
    // ohne Tabelle bleibt die Map leer, das Blatt kommt ohne Bild.
    const [mediaByProject, setMediaByProject] = useState<Map<string, ProjectMedia[]>>(new Map());
    useEffect(() => {
        let alive = true;
        listAllProjectMedia()
            .then(res => { if (alive) setMediaByProject(res.byProject); })
            .catch(e => console.warn('[referenzblatt] Projektbilder nicht ladbar:', e));
        return () => { alive = false; };
    }, []);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
    };

    // Frueher stand hier eine EIGENE Kopie von getNormalizedServices ohne den
    // Deckungsstempel der Monatspauschale. Jetzt liest die Ansicht denselben
    // Pfad wie die Buchhaltung (projectLv.getNormalizedServices) — gedeckte
    // Positionen tragen damit ihren Hinweis auch hier.
    const getProjectVolume = (p: ListProject): number => {
        const services = getNormalizedServices(p);
        let total = 0;
        services.forEach(s => {
            // NEP zaehlt erst, was gebucht ist (contractedQuantity).
            total += contractedQuantity(s) * s.unitPrice;
        });
        if (services.length === 0) {
             const meta = p.data?.metadata as ReportMetadata | undefined;
             const scope = meta?.globalData?.scope;
             if (scope && scope.totalInspections > 0 && scope.pricePerInspection) { 
                 total += scope.totalInspections * scope.pricePerInspection; 
             }
        }
        return total;
    };

    // Extract raw project properties safely
    const processedProjects = useMemo(() => {
        return projects.map(p => {
            const meta = p.data?.metadata as ReportMetadata | undefined;
            const cat = p.category || meta?.projectCategory || meta?.category || '';
            const sub = p.subcategory || meta?.projectSubcategory || meta?.subcategory || '';
            const coord = p.coordinator_name || meta?.coordinator || '';
            const pNumber = p.project_number || meta?.projectNumber || '';
            const pName = p.name || meta?.projectName || '';
            const totalProjVol = meta?.totalProjectVolume;
            const ourVolume = getProjectVolume(p);
            const pVolume = (totalProjVol !== undefined && totalProjVol !== null && totalProjVol !== 0)
                ? Number(totalProjVol)
                : ourVolume;
            
            // Period from globalData
            const startDate = meta?.globalData?.startDate || '';
            const endDate = meta?.globalData?.endDate || '';
            const address = p.address || meta?.projectAddress || '';
            const isFinished = isProjectFinished(p);

            return {
                raw: p,
                id: p.id,
                projectNumber: pNumber,
                name: pName,
                category: cat,
                subcategory: sub,
                coordinator: coord,
                volume: pVolume,
                ourContractVolume: ourVolume,
                totalProjectVolume: totalProjVol,
                startDate,
                endDate,
                address,
                isFinished,
                services: getNormalizedServices(p),
                description: (meta?.projectDescription || '').trim(),
                image: titelbildVon(mediaByProject.get(p.id) || []),
                imageCount: (mediaByProject.get(p.id) || []).length
            };
        });
    }, [projects, mediaByProject]);

    // Available Coordinators for selection
    const coordinators = useMemo(() => {
        const list = new Set<string>();
        processedProjects.forEach(p => {
            if (p.coordinator) list.add(p.coordinator);
        });
        return Array.from(list).filter(Boolean).sort();
    }, [processedProjects]);

    // Available Subcategories based on selected Category
    const subcategories = useMemo(() => {
        if (!selectedCategory) return [];
        return projectCategories[selectedCategory] || [];
    }, [selectedCategory, projectCategories]);

    // Filtering logic
    const filteredProjects = useMemo(() => {
        return processedProjects.filter(p => {
            // Search filter
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const matchName = p.name.toLowerCase().includes(searchLower);
                const matchNumber = p.projectNumber.toLowerCase().includes(searchLower);
                const matchAddress = p.address.toLowerCase().includes(searchLower);
                if (!matchName && !matchNumber && !matchAddress) return false;
            }

            // Category filter
            if (selectedCategory && p.category !== selectedCategory) return false;

            // Subcategory filter
            if (selectedSubcategory && p.subcategory !== selectedSubcategory) return false;

            // Coordinator filter
            if (selectedCoordinator && p.coordinator !== selectedCoordinator) return false;

            // Status filter
            if (statusFilter === 'active' && p.isFinished) return false;
            if (statusFilter === 'finished' && !p.isFinished) return false;

            // Volume bounds
            if (volumeRange === 'lt_10k' && p.volume >= 10000) return false;
            if (volumeRange === '10k_50k' && (p.volume < 10000 || p.volume > 50000)) return false;
            if (volumeRange === '50k_150k' && (p.volume < 50000 || p.volume > 150000)) return false;
            if (volumeRange === 'gt_150k' && p.volume <= 150000) return false;
            if (volumeRange === 'custom') {
                const minVal = parseFloat(minVolume);
                const maxVal = parseFloat(maxVolume);
                if (!isNaN(minVal) && p.volume < minVal) return false;
                if (!isNaN(maxVal) && p.volume > maxVal) return false;
            }

            return true;
        });
    }, [processedProjects, searchTerm, selectedCategory, selectedSubcategory, selectedCoordinator, statusFilter, volumeRange, minVolume, maxVolume]);

    // Handle Selection Toggles
    const handleToggleSelect = (id: string) => {
        const next = new Set(selectedProjectIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedProjectIds(next);
    };

    const handleSelectAll = () => {
        if (selectedProjectIds.size === filteredProjects.length) {
            setSelectedProjectIds(new Set());
        } else {
            setSelectedProjectIds(new Set(filteredProjects.map(p => p.id)));
        }
    };

    const selectedPreviewProject = useMemo(() => {
        if (!previewProjectId) return null;
        return processedProjects.find(p => p.id === previewProjectId) || null;
    }, [previewProjectId, processedProjects]);

    // MONATSPAUSCHALE: Hinweise fuer den Leistungsauszug der Live-Vorschau —
    // dieselbe Ableitung, die auch der PDF-Export je Projekt rechnet.
    const previewFlatHints = useMemo(
        () => flatCoverageHints(selectedPreviewProject?.services || []),
        [selectedPreviewProject]
    );

    // Helper: format German dates
    const formatDate = (isoStr: string) => {
        if (!isoStr) return '-';
        try {
            const d = new Date(isoStr);
            if (isNaN(d.getTime())) return isoStr;
            return d.toLocaleDateString('de-DE');
        } catch {
            return isoStr;
        }
    };

    // PDF Single / Batch Export Generation
    const generatePdf = async (projectIdsToExport: string[]) => {
        if (projectIdsToExport.length === 0) {
            alert('Bitte wählen Sie mindestens ein Projekt für den Export aus.');
            return;
        }
        if (pdfBusy) return;
        setPdfBusy(true);

        // Bilder VOR dem Zeichnen laden (jsPDF ist synchron). Nicht ladbare
        // Bilder fallen still weg — das Blatt kommt trotzdem.
        const pdfImages = new Map<string, { dataUrl: string; width: number; height: number }>();
        if (showImageInPdf) {
            for (const id of projectIdsToExport) {
                const p = processedProjects.find(item => item.id === id);
                if (!p?.image) continue;
                const loaded = await loadReferenceImageForPdf(p.image);
                if (loaded) pdfImages.set(id, loaded);
            }
        }

        try {
        const doc = new jsPDF('p', 'mm', 'a4');
        const count = projectIdsToExport.length;

        projectIdsToExport.forEach((id, index) => {
            const p = processedProjects.find(item => item.id === id);
            if (!p) return;

            if (index > 0) {
                doc.addPage();
            }

            // 1. Header Band
            doc.setFillColor(30, 41, 59);
            doc.rect(0, 0, 210, 45, 'F');

            // Header text
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(22);
            doc.text('PROJEKT-REFERENZBLATT', 15, 20);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            const subtext = p.isFinished ? 'STAMP: ABGESCHLOSSENE REFERENZ' : 'STAMP: LAUFENDES PROJEKT';
            doc.text(subtext, 15, 28);

            // App name / Branding
            doc.setFontSize(8);
            doc.setFont('helvetica', 'italic');
            doc.text('SiGeKo Smart Report v2.5 - Verwaltungscenter', 150, 15);

            // Project Number Badge
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setFillColor(59, 130, 246);
            doc.rect(145, 24, 50, 10, 'F');
            doc.setTextColor(255, 255, 255);
            doc.text(`Nr. ${p.projectNumber || 'N/A'}`, 150, 30);

            // 2. Project Title section
            doc.setTextColor(15, 23, 42); // slate 900
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(p.name, 15, 60);

            // Draw clean separator line
            doc.setDrawColor(226, 232, 240); // slate 200
            doc.setLineWidth(0.5);
            doc.line(15, 65, 195, 65);

            // 3. Metadata Table (Two-column layout or autoTable grid)
            doc.setFontSize(12);
            doc.setTextColor(30, 41, 59);
            doc.text('Stammdaten & Details', 15, 75);

            // Meta table fields
            const tempMetaRows = [
                ['Bauherr', p.raw.data?.metadata?.clientName || '-'],
                ['Auftraggeber', p.raw.data?.metadata?.customerName || '-'],
                ['Standort / Adresse', p.address || '-'],
                ['Kategorie', p.category || '-'],
                ['Unterkategorie', p.subcategory || '-'],
                ['Koordinator', p.coordinator || '-'],
                ['Projekt-Zeitraum', `${formatDate(p.startDate)} - ${formatDate(p.endDate) || 'Fortlaufend'}`],
                ['Status', p.isFinished ? 'Abgeschlossen' : 'In Bearbeitung']
            ];

            if (showFinancialsInPdf) {
                if (p.totalProjectVolume !== undefined && p.totalProjectVolume !== null && p.totalProjectVolume !== 0) {
                    tempMetaRows.push(['Gesamt-Projektvolumen (Bausumme)', formatCurrency(p.volume)]);
                    tempMetaRows.push(['SiGeKo-Honorar (Netto)', formatCurrency(p.ourContractVolume)]);
                } else {
                    tempMetaRows.push(['SiGeKo-Honorar (Netto)', formatCurrency(p.volume)]);
                }
            }

            autoTable(doc, {
                startY: 80,
                head: [],
                body: tempMetaRows,
                theme: 'plain',
                styles: {
                    fontSize: 10,
                    cellPadding: 3,
                },
                // Spaltenbreiten bestimmt autoTable selbst. Die frueheren
                // `width`-Angaben kennt jspdf-autotable nicht (dort heisst es
                // `cellWidth`) und haben nie gewirkt — entfernt, Layout gleich.
                columnStyles: {
                    0: { fontStyle: 'bold' }
                },
                margin: { left: 15, right: 15 }
            });

            let currentY = (doc as any).lastAutoTable.finalY + 15;

            // Seite voll? Dann weiter auf einer neuen — Fusszeile bleibt frei.
            const ensureSpace = (needed: number) => {
                if (currentY + needed > 275) {
                    doc.addPage();
                    currentY = 20;
                }
            };

            // 3b. Projektbeschreibung (Social-Media-Text)
            if (p.description) {
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(30, 41, 59);
                ensureSpace(20);
                doc.text('Projektbeschreibung', 15, currentY);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.setTextColor(51, 65, 85);
                const lines: string[] = doc.splitTextToSize(p.description, 180);
                const lineHeight = 5;
                let i = 0;
                currentY += 6;
                while (i < lines.length) {
                    ensureSpace(lineHeight);
                    doc.text(lines[i], 15, currentY);
                    currentY += lineHeight;
                    i++;
                }
                currentY += 8;
            }

            // 3c. Referenzbild
            const pdfImage = pdfImages.get(id);
            if (pdfImage) {
                const fit = fitImageIntoBox(pdfImage.width, pdfImage.height, 180, 95);
                if (fit) {
                    ensureSpace(fit.h + 12);
                    doc.setFontSize(12);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(30, 41, 59);
                    doc.text('Projektbild', 15, currentY);
                    currentY += 4;
                    doc.addImage(pdfImage.dataUrl, 'JPEG', 15, currentY, fit.w, fit.h);
                    currentY += fit.h + 12;
                }
            }

            // 4. Performed Services section
            if (showServicesInPdf && p.services.length > 0) {
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('Erbringung / Beauftragte Leistungen', 15, currentY);

                const serviceHeader = ['Pos.', 'Bezeichnung', 'Menge', 'Einheit'];
                if (showFinancialsInPdf) {
                    serviceHeader.push('E-Preis (Netto)', 'Gesamt (Netto)');
                }

                // MONATSPAUSCHALE: gedeckte Positionen tragen den Hinweis auch
                // auf dem Referenzblatt — die beauftragten Werte bleiben, aber
                // niemand soll die Position fuer separat verguetet halten.
                const serviceFlatHints = flatCoverageHints(p.services);
                const serviceRows = p.services.map(s => {
                    const flatHint = serviceFlatHints.get(s.id);
                    const row = [
                        s.lvPosition || '-',
                        flatHint ? `${s.title || '-'}\n${flatHint}` : (s.title || '-'),
                        contractedQuantity(s),
                        s.unit || 'Stück'
                    ];
                    if (showFinancialsInPdf) {
                        row.push(
                            formatCurrency(s.unitPrice),
                            formatCurrency(contractedQuantity(s) * s.unitPrice)
                        );
                    }
                    return row;
                });

                autoTable(doc, {
                    startY: currentY + 5,
                    head: [serviceHeader],
                    body: serviceRows,
                    theme: 'striped',
                    headStyles: {
                        fillColor: [51, 65, 85],
                        textColor: [255, 255, 255],
                        fontSize: 9,
                        fontStyle: 'bold'
                    },
                    styles: {
                        fontSize: 8.5,
                        cellPadding: 2.5
                    },
                    // Wie oben: `width` war wirkungslos und ist entfernt. Als
                    // `cellWidth` waeren es ohne Preise 190 mm auf 180 mm Satzbreite.
                    columnStyles: {
                        2: { halign: 'center' },
                        3: { halign: 'center' },
                        4: { halign: 'right' },
                        5: { halign: 'right' }
                    },
                    margin: { left: 15, right: 15 }
                });
            }

            // Footer of each page
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184); // slate 400
            doc.text(`Referenzblatt generiert am ${new Date().toLocaleDateString('de-DE')} • Seite ${index + 1} von ${count}`, 15, 285);
        });

        const filename = count === 1 
            ? `Referenzblatt_${processedProjects.find(item => item.id === projectIdsToExport[0])?.projectNumber || 'Ref'}.pdf`
            : `Referenzmappe_${new Date().toISOString().split('T')[0]}.pdf`;

        doc.save(filename);
        } finally {
            setPdfBusy(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Filtering Console Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
                    <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                        <Filter className="w-5 h-5 text-indigo-600" /> Referenzgenerator-Filter
                    </h3>
                    <div className="text-xs text-slate-500 font-medium">
                        {filteredProjects.length} von {processedProjects.length} Projekten matchen die Kriterien
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Search & Status Row */}
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Suche</label>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Projekt, Nr., Adresse..."
                                    className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                                />
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Projektstatus</label>
                            <div className="flex gap-1.5 bg-slate-100 p-1 rounded-xl">
                                {(['all', 'active', 'finished'] as const).map(opt => (
                                    <button
                                        key={opt}
                                        onClick={() => setStatusFilter(opt)}
                                        className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${
                                            statusFilter === opt
                                                ? 'bg-white text-slate-800 shadow-sm'
                                                : 'text-slate-500 hover:text-slate-800'
                                        }`}
                                    >
                                        {opt === 'all' ? 'Alle' : opt === 'active' ? 'Aktiv' : 'Abgeschlossen'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Category Selection Row */}
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Kategorie</label>
                            <select
                                value={selectedCategory}
                                onChange={e => {
                                    setSelectedCategory(e.target.value);
                                    setSelectedSubcategory('');
                                }}
                                className="w-full px-3 py-2 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                            >
                                <option value="">-- Alle Kategorien --</option>
                                {Object.keys(projectCategories).map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Unterkategorie</label>
                            <select
                                value={selectedSubcategory}
                                onChange={e => setSelectedSubcategory(e.target.value)}
                                disabled={!selectedCategory}
                                className="w-full px-3 py-2 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium disabled:bg-slate-100 disabled:text-slate-400"
                            >
                                <option value="">-- Alle Unterkategorien --</option>
                                {subcategories.map(sub => (
                                    <option key={sub} value={sub}>{sub}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Coordinator and Volume Section */}
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Koordinator</label>
                            <select
                                value={selectedCoordinator}
                                onChange={e => setSelectedCoordinator(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                            >
                                <option value="">-- Alle Koordinatoren --</option>
                                {coordinators.map(coord => (
                                    <option key={coord} value={coord}>{coord}</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block">Projektvolumen</label>
                            <select
                                value={volumeRange}
                                onChange={e => setVolumeRange(e.target.value as any)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                            >
                                <option value="all">Unbegrenzt (Alle)</option>
                                <option value="lt_10k">&lt; 10.000 €</option>
                                <option value="10k_50k">10.000 € - 50.000 €</option>
                                <option value="50k_150k">50.000 € - 150.000 €</option>
                                <option value="gt_150k">&gt; 150.000 €</option>
                                <option value="custom">Benutzerdefiniert...</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Custom volume input bound fields */}
                {volumeRange === 'custom' && (
                    <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100 max-w-md animate-in slide-in-from-top-1 duration-200">
                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-slate-400 uppercase">Mindestvolumen (€)</label>
                            <input
                                type="number"
                                value={minVolume}
                                onChange={e => setMinVolume(e.target.value)}
                                placeholder="0"
                                className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[11px] font-bold text-slate-400 uppercase">Maximalvolumen (€)</label>
                            <input
                                type="number"
                                value={maxVolume}
                                onChange={e => setMaxVolume(e.target.value)}
                                placeholder="Unbegrenzt"
                                className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Print Settings Preferences */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-wrap gap-x-6 gap-y-2 items-center text-sm text-slate-600">
                <span className="font-bold text-xs uppercase text-slate-500 tracking-wider">PDF-Optionen:</span>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input 
                        type="checkbox" 
                        checked={showFinancialsInPdf} 
                        onChange={() => setShowFinancialsInPdf(!showFinancialsInPdf)}
                        className="rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                    />
                    <span>Finanzvolumen einblenden</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input 
                        type="checkbox" 
                        checked={showServicesInPdf} 
                        onChange={() => setShowServicesInPdf(!showServicesInPdf)}
                        className="rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                    />
                    <span>Erbringungsverzeichnis (LV-Positionen) ausgeben</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={showImageInPdf}
                        onChange={() => setShowImageInPdf(!showImageInPdf)}
                        className="rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                    />
                    <span>Titelbild einbetten</span>
                </label>
            </div>

            {/* Results Grid View / Actionbar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* List Column */}
                <div className="lg:col-span-2 space-y-3">
                    <div className="flex justify-between items-center py-1">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleSelectAll}
                                className="text-slate-500 hover:text-slate-800 transition"
                            >
                                {selectedProjectIds.size === filteredProjects.length && filteredProjects.length > 0 ? (
                                    <span className="flex items-center gap-1.5 text-xs font-bold text-indigo-600">
                                        <CheckSquare className="w-4 h-4" /> Alle abwählen
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
                                        <Square className="w-4 h-4" /> Alle auswählen
                                    </span>
                                )}
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                disabled={selectedProjectIds.size === 0 || pdfBusy}
                                onClick={() => generatePdf(Array.from(selectedProjectIds))}
                                variant="primary"
                                className="shadow-sm py-1.5 px-3 text-xs flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                            >
                                <Download className="w-3.5 h-3.5" /> {pdfBusy ? 'Erstelle PDF …' : `Referenzmappe drucken (${selectedProjectIds.size})`}
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                        {filteredProjects.length === 0 ? (
                            <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-400 font-medium">
                                Keine Projekte entsprechen den Filterkriterien.
                            </div>
                        ) : (
                            filteredProjects.map(p => {
                                const isSelected = selectedProjectIds.has(p.id);
                                return (
                                    <div 
                                        key={p.id}
                                        onClick={() => setPreviewProjectId(p.id)}
                                        className={`bg-white border rounded-xl p-4 shadow-sm hover:border-slate-300 hover:shadow transition-all relative cursor-pointer flex items-start gap-3 justify-between ${
                                            previewProjectId === p.id ? 'ring-2 ring-indigo-500 border-indigo-500 ' : ''
                                        }${isSelected ? 'bg-indigo-50/20' : ''}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            {/* Checkbox handler */}
                                            <div 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleSelect(p.id);
                                                }}
                                                className="mt-1 flex-shrink-0 cursor-pointer text-slate-400 hover:text-indigo-600 transition"
                                            >
                                                {isSelected ? (
                                                    <CheckSquare className="w-5 h-5 text-indigo-600" />
                                                ) : (
                                                    <Square className="w-5 h-5" />
                                                )}
                                            </div>

                                            {p.image ? (
                                                <img src={p.image.url} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200 flex-shrink-0" loading="lazy" />
                                            ) : (
                                                <div className="w-16 h-16 rounded-lg bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-slate-300 flex-shrink-0" title="Kein Projektbild">
                                                    <ImageOff className="w-5 h-5" />
                                                </div>
                                            )}

                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-bold">
                                                        {p.projectNumber || 'N/A'}
                                                    </span>
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                                        p.isFinished 
                                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                                                            : 'bg-blue-50 text-blue-700 border border-blue-200'
                                                    }`}>
                                                        {p.isFinished ? 'Abgeschlossen' : 'Aktiv'}
                                                    </span>
                                                </div>
                                                <h4 className="font-bold text-slate-800 text-sm leading-snug line-clamp-1">{p.name}</h4>
                                                <div className="text-xs text-slate-400 flex items-center gap-1.5">
                                                    <MapPin className="w-3.5 h-3.5" />
                                                    <span className="line-clamp-1">{p.address || 'Keine Adresse'}</span>
                                                </div>
                                                <div className="flex flex-wrap gap-y-1 gap-x-3 text-xs text-slate-500 font-medium pt-1.5">
                                                    <span className="flex items-center gap-1 text-[11px]"><Tag className="w-3.5 h-3.5 text-blue-500" /> {p.category || 'Keine Kat.'}</span>
                                                    <span className="flex items-center gap-1 text-[11px]"><User className="w-3.5 h-3.5 text-emerald-500" /> {p.coordinator || 'Unbekannt'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="text-right flex flex-col justify-between items-end h-full">
                                            <div className="text-xs font-mono font-bold text-slate-700">
                                                {formatCurrency(p.volume)}
                                            </div>
                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mt-1">
                                                {p.totalProjectVolume !== undefined && p.totalProjectVolume !== null && p.totalProjectVolume !== 0 ? 'Gesamt-Bauvolumen' : 'SiGeKo-Honorar'}
                                            </span>
                                            <ChevronRight className="w-5 h-5 text-slate-400 mt-4" />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Interactive Live Preview Panel */}
                <div className="bg-slate-800 text-slate-100 rounded-2xl shadow-lg border border-slate-700 p-6 flex flex-col justify-between min-h-[500px]">
                    {selectedPreviewProject ? (
                        <div className="space-y-6 flex-1 flex flex-col justify-between overflow-hidden">
                            <div className="space-y-4 overflow-y-auto pr-1">
                                <div className="border-b border-slate-700 pb-4">
                                    <div className="text-indigo-400 text-xs uppercase font-bold tracking-widest flex items-center gap-2">
                                        <BookOpen className="w-4 h-4" /> LIVE REFERENZBLATT VORSCHAU
                                    </div>
                                    <h3 className="font-bold text-lg text-white mt-2 leading-tight">{selectedPreviewProject.name}</h3>
                                    <div className="text-xs font-mono text-slate-400 mt-1">
                                        Referenznummer: {selectedPreviewProject.projectNumber || 'N/A'}
                                    </div>
                                </div>

                                {selectedPreviewProject.image ? (
                                    <a href={selectedPreviewProject.image.url} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
                                        <img src={selectedPreviewProject.image.url} alt="" className="w-full max-h-44 object-cover" loading="lazy" />
                                        <div className="text-[10px] text-slate-400 px-2 py-1 flex items-center gap-1.5">
                                            <Camera className="w-3 h-3" />
                                            {selectedPreviewProject.image.width && selectedPreviewProject.image.height ? `${selectedPreviewProject.image.width} × ${selectedPreviewProject.image.height} px · ` : ''}{describeFileSize(selectedPreviewProject.image.sizeBytes)}{selectedPreviewProject.imageCount > 1 ? ` · Titelbild von ${selectedPreviewProject.imageCount}` : ''}
                                        </div>
                                    </a>
                                ) : (
                                    <div className="rounded-xl border border-dashed border-slate-700 text-slate-500 text-xs px-3 py-3 flex items-center gap-2">
                                        <ImageOff className="w-4 h-4" /> Kein Projektbild — im Projekt unter „Projektbeschreibung & Projektbilder" oder unter Kataloge & Vorlagen → Projektbilder hochladen.
                                    </div>
                                )}

                                <div className="space-y-3.5 text-sm font-medium">
                                    <div className="flex justify-between border-b border-slate-700 py-1.5">
                                        <span className="text-slate-400 uppercase text-[11px] tracking-wider">Bauherr</span>
                                        <span className="text-slate-200 text-right max-w-[150px] truncate">{selectedPreviewProject.raw.data?.metadata?.clientName || '-'}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-slate-700 py-1.5">
                                        <span className="text-slate-400 uppercase text-[11px] tracking-wider">Standort</span>
                                        <span className="text-slate-200 text-right max-w-[150px] truncate">{selectedPreviewProject.address || '-'}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-slate-700 py-1.5">
                                        <span className="text-slate-400 uppercase text-[11px] tracking-wider">Kategorie</span>
                                        <span className="text-slate-200">{selectedPreviewProject.category || '-'}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-slate-700 py-1.5">
                                        <span className="text-slate-400 uppercase text-[11px] tracking-wider">Koordinator</span>
                                        <span className="text-slate-200">{selectedPreviewProject.coordinator || '-'}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-slate-700 py-1.5">
                                        <span className="text-slate-400 uppercase text-[11px] tracking-wider">Zeitraum</span>
                                        <span className="text-slate-200">{formatDate(selectedPreviewProject.startDate)} - {formatDate(selectedPreviewProject.endDate) || 'Fortlaufend'}</span>
                                    </div>
                                    {selectedPreviewProject.description && (
                                        <div className="py-1.5 border-b border-slate-700 space-y-1">
                                            <span className="text-slate-400 uppercase text-[11px] tracking-wider block">Projektbeschreibung</span>
                                            <p className="text-slate-300 text-xs leading-relaxed whitespace-pre-line line-clamp-6" title={selectedPreviewProject.description}>{selectedPreviewProject.description}</p>
                                        </div>
                                    )}
                                    {showFinancialsInPdf && (
                                        <div className="space-y-1.5 py-1.5 border-b border-slate-700">
                                            {selectedPreviewProject.totalProjectVolume !== undefined && selectedPreviewProject.totalProjectVolume !== null && selectedPreviewProject.totalProjectVolume !== 0 ? (
                                                <>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-amber-400 uppercase text-[11px] tracking-wider font-bold">Gesamt-Projektvolumen</span>
                                                        <span className="text-white font-mono font-bold text-base">{formatCurrency(selectedPreviewProject.volume)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center text-xs text-slate-400 mt-1">
                                                        <span className="uppercase text-[10px] tracking-wider">SiGeKo-Honorar (Beauftragt)</span>
                                                        <span className="font-mono">{formatCurrency(selectedPreviewProject.ourContractVolume)}</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-indigo-400 uppercase text-[11px] tracking-wider font-bold">SiGeKo-Honorar (Netto)</span>
                                                    <span className="text-white font-mono font-bold text-base">{formatCurrency(selectedPreviewProject.volume)}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {showServicesInPdf && selectedPreviewProject.services.length > 0 && (
                                    <div className="space-y-2 mt-2">
                                        <span className="text-slate-400 uppercase text-[11px] tracking-wider font-bold block">Leistungsauszug ({selectedPreviewProject.services.length})</span>
                                        <div className="text-xs bg-slate-900 border border-slate-700 rounded-xl p-3 max-h-[140px] overflow-y-auto space-y-2">
                                            {selectedPreviewProject.services.map(s => (
                                                <div key={s.id} className="text-slate-300 font-medium">
                                                    <div className="flex justify-between">
                                                        <span className="truncate max-w-[120px]">{s.title}</span>
                                                        <span className="text-slate-400 font-mono">{contractedQuantity(s)} {s.unit}</span>
                                                    </div>
                                                    {previewFlatHints.has(s.id) && (
                                                        <div className="text-[10px] text-indigo-300 truncate" title={previewFlatHints.get(s.id)}>
                                                            {previewFlatHints.get(s.id)}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="pt-4 border-t border-slate-700 mt-auto">
                                <Button
                                    onClick={() => generatePdf([selectedPreviewProject.id])}
                                    variant="primary"
                                    className="w-full shadow py-2.5 bg-blue-600 hover:bg-blue-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 text-sm"
                                >
                                    <Download className="w-4 h-4" /> Referenzblatt exportieren
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center text-center h-full text-slate-500 p-8 space-y-4 my-auto">
                            <div className="p-4 bg-slate-700/50 rounded-2xl text-slate-400">
                                <FileText className="w-10 h-10" />
                            </div>
                            <div>
                                <h4 className="font-bold text-white text-base">Referenzblatt-Preview</h4>
                                <p className="text-xs text-slate-400 mt-1 max-w-[200px] mx-auto">
                                    Wählen Sie ein Projekt links aus der Liste, um eine Live-Ausgabevorschau zu sehen.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};
